import React, { Component } from "react";
import { Pricing } from "../pricing";
import { LEPPumpViewModel } from "./common/LEPPumpViewModel";
import { getControlPanels, getDSAFloats, getDualSealCapKitsForControlPanel, getDualSealCapKitsNoControlPanel, getGuideRails, getSingleSealCapKits, printPacket } from "../bridgeAPIService";
import { Button } from "./common/Button";
import { PriceDisplay } from "./common/PriceDisplay";

interface AccessoryDisplayProps extends CompleteProps {
    viewModel?: LEPPumpViewModel;
    pricing: Pricing;
    pumpSelected?: () => void;
}

interface AccessoryDisplayState {
    controlPanelChoice: boolean;
    capKitChoice: boolean;
    floatChoice: boolean;
    guideRailChoice: boolean;
    complete: boolean;
    controlPanelChoices: Accessory[];
    capKitChoices: Accessory[];
    floatChoices: Accessory[];
    guideRailChoices: Accessory[];
    chosenControlPanel: Nullable<Accessory>;
    chosenCapKit: Nullable<Accessory>;
    chosenFloat: Nullable<Accessory>;
    chosenGuideRail: Nullable<Accessory>;
    viewModelAccessory: Nullable<Accessory>;
}

/**
 * The AccessoryDisplay Component is designed to display all accessories 
 * available for the model configured by the user. This will guide the user
 * through the selection process to present them with all options.
 * 
 * A diagram depicting the logic flow can be found here: https://libertypumps.atlassian.net/l/cp/t21tS4y4
 */
export class AccessoryDisplay extends Component<AccessoryDisplayProps, AccessoryDisplayState> {
    constructor(props: AccessoryDisplayProps) {
        super(props);
        this.controlPanelChosen = this.controlPanelChosen.bind(this);
        this.resetToControlPanelChoice = this.resetToControlPanelChoice.bind(this);
        this.skipControlPanelChoice = this.skipControlPanelChoice.bind(this);
        this.capKitChosen = this.capKitChosen.bind(this);
        this.resetToCapKitChoice = this.resetToCapKitChoice.bind(this);
        this.floatChosen = this.floatChosen.bind(this);
        this.resetToFloatChoice = this.resetToFloatChoice.bind(this);
        this.guideRailChosen = this.guideRailChosen.bind(this);
        this.resetToGuideRailChoice = this.resetToGuideRailChoice.bind(this);
        this.printPacket = this.printPacket.bind(this);
        this.state = {
            controlPanelChoice: true,
            capKitChoice: false,
            floatChoice: false,
            guideRailChoice: false,
            complete: false,
            controlPanelChoices: [],
            capKitChoices: [],
            floatChoices: [],
            guideRailChoices: [],
            chosenControlPanel: null,
            chosenCapKit: null,
            chosenFloat: null,
            chosenGuideRail: null,
            viewModelAccessory: {
                Code: this.props.viewModel ? this.props.viewModel.getModelNumber() : '',
                Description: ''
            }
        };
    }

    /**
     * When the component loads, start by getting all control panels for the 
     * configured model.
     */
    componentDidMount(): void {
        if (!this.props.viewModel) {
            return; 
        }
        getControlPanels(this.props.viewModel).then(r => {
            if (r.length > 0) {
                this.setState({controlPanelChoices: r});
                return;
            }
            this.controlPanelChosen(NoControlPanel);
        });     
        getGuideRails(this.props.viewModel).then(r => this.setState({guideRailChoices: r}));
    }

    /**
     * Event Handler for when a control panel is chosen. Refer to logic diagram
     * for instruction on how to proceed.
     * @param choice the chosen control panel
     */
    private controlPanelChosen(choice: Accessory): void {
        this.setState({
            chosenControlPanel: choice,
            controlPanelChoice: false
        }, () => {getDSAFloats(choice.Code).then(r => this.setState({floatChoices: r}))});
        
        // Special case - 2.5 hp grinders use the same cap kits as single seal pumps
        if (this.props.viewModel?.isSingleSeal || this.props.viewModel?.isSmallGrinder) {
            // are cap kits available?
            getSingleSealCapKits(this.props.viewModel).then(r => 
                this.setState({capKitChoices: r}, () => {
                    // callback used to check these state variables after they've finished setting.
                    // this was previously a race condition due to not waiting for setState to complete.
                    const capKitsAvailable: boolean = this.state.capKitChoices.length > 0;
                    const guideRailsAvailable: boolean = this.state.guideRailChoices.length > 0;
                    this.setState({
                        capKitChoice: capKitsAvailable,
                        guideRailChoice: !capKitsAvailable && guideRailsAvailable,
                        complete: !capKitsAvailable && !guideRailsAvailable
                    });
                })
            );
            return;
        }
        // dual seal

        // did we skip control panel selection?
        if (choice === NoControlPanel) {
            // is voltage code '8' or '2'?
            if (this.props.viewModel?.isThreePhase) {
                // jump to guide rails
                this.setState({
                    capKitChoices: [],
                    guideRailChoice: true
                });
                return;
            }
            getDualSealCapKitsNoControlPanel(this.props.viewModel).then(r => this.setState({capKitChoices: r}, () => this.setState({capKitChoice: true})));
            return;
        }

        // we did not skip control panel selection
        // is voltage code '8' or '2'?
        if ((this.props.viewModel?.isThreePhase) ||
            choice.Code.endsWith('A')) {
            // jump to floats selection
            this.setState({
                capKitChoices: [],
                floatChoice: true
            });
            return;
        }
        getDualSealCapKitsForControlPanel(this.props.viewModel).then(r => this.setState({capKitChoices: r}, () => this.setState({capKitChoice: true})));
    }

    /**
     * Event handler to reset accessory selections all the way back to the control panel choice.
     */
    private resetToControlPanelChoice(): void {
        this.setState({
            controlPanelChoice: true,
            chosenControlPanel: null,
            capKitChoice: false,
            chosenCapKit: null,
            floatChoice: false,
            chosenFloat: null,
            guideRailChoice: false,
            chosenGuideRail: null,
            complete: false
        });
    }

    /**
     * Simluate no control panel being chosen in order to utilize event handler logic
     */
    private skipControlPanelChoice(): void {
        this.controlPanelChosen(NoControlPanel);
    }

    /**
     * Event Handler for when a capactior kit is chosen. Refer to logic diagram
     * for instruction on how to proceed.
     * @param choice the chosen capacitor kit
     */
    private capKitChosen(choice: Accessory): void {
        this.setState({
            chosenCapKit: choice,
            capKitChoice: false
        });
        // Special case - 2.5 hp grinders should be treated the same as single seal pumps
        if (this.props.viewModel?.isSingleSeal || this.props.viewModel?.isSmallGrinder || this.state.chosenControlPanel === NoControlPanel) {
            this.setState({
                guideRailChoice: true
            });
            return;
        }
        this.setState({
            floatChoice: true
        });
    }

    /**
     * Event handler to reset accessory selections back to the capacitor kit choice.
     */
    private resetToCapKitChoice(): void {
        this.setState({
            capKitChoice: true,
            chosenCapKit: null,
            floatChoice: false,
            chosenFloat: null,
            guideRailChoice: false,
            chosenGuideRail: null,
            complete: false
        });
    }

    /**
     * Event Handler for when a float choice is made. Refer to logic diagram 
     * for instruction on how to proceed.
     * @param choice the chosen float
     */
    private floatChosen(choice: Accessory): void {
        this.setState({
            chosenFloat: choice,
            floatChoice: false,
            guideRailChoice: true
        });
    }
    
    /**
     * Event handler to reset accessory selections back to the float choice.
     */
    private resetToFloatChoice(): void {
        this.setState({
            floatChoice: true,
            chosenFloat: null,
            guideRailChoice: false,
            chosenGuideRail: null,
            complete: false
        });
    }

    /**
     * Event Handler for when a guide rail choice is made. Refer to logic 
     * diagram for instruction on how to proceed.
     * @param choice the chosen guide rail
     */
    private guideRailChosen(choice: Accessory): void {
        this.setState({
            chosenGuideRail: choice,
            guideRailChoice: false,
            complete: true
        });
    }
    
    /**
     * Event handler to reset accessory selections  back to the guide rail choice.
     */
    private resetToGuideRailChoice(): void {
        this.setState({
            guideRailChoice: true,
            chosenGuideRail: null,
            complete: false
        });
    }

    /**
     * Event Handler to print the literature packet for the configured LEP Pump
     * as well as all accessories chosen.
     */
    private async printPacket(): Promise<void> {
        if (!this.props.viewModel) {
            return;
        }
        await printPacket(this.props.viewModel, 
                          this.state.chosenControlPanel ? this.state.chosenControlPanel.Code : '', 
                          this.state.chosenCapKit? this.state.chosenCapKit.Code : '', 
                          this.state.chosenFloat? this.state.chosenFloat.Code : '', 
                          this.state.chosenGuideRail? this.state.chosenGuideRail.Code : '', 
                          this.props.pricing);
    }

    render() {
        return (
            <div id='accessory-display' className='bap-row'>
                <div id='sidebar' className='bap-col-3'>
                    <div id='backBtn' className='bap-row'>
                        <div className='bap-col-4'>
                            <Button cssClass='btn btn-sm btn-pump-choice' clicked={this.props.pumpSelected ? this.props.pumpSelected : () => {}}>Back</Button>
                        </div>
                    </div>
                    <ul className='list-group'>
                        {/* Show pump first */}
                        <AccessorySummary accessoryType='Build-A-Pump' accessory={this.state.viewModelAccessory} accessoryClicked={this.props.pumpSelected ? this.props.pumpSelected : () => {}} pricing={this.props.pricing} active={false} choiceCount={-1} />
                        
                        {/* Control Panels */}
                        <AccessorySummary accessoryType='Control Panel' accessory={this.state.chosenControlPanel} accessoryClicked={this.resetToControlPanelChoice} pricing={this.props.pricing} active={this.state.controlPanelChoice} choiceCount={this.state.controlPanelChoices.length}/>

                        {/* Cap Kits */}
                        <AccessorySummary accessoryType='Cap Kit' accessory={this.state.chosenCapKit} accessoryClicked={this.resetToCapKitChoice} pricing={this.props.pricing} active={this.state.capKitChoice} choiceCount={this.state.capKitChoices.length}/>

                        {/* Floats */}
                        <AccessorySummary accessoryType='Float' accessory={this.state.chosenFloat} accessoryClicked={this.resetToFloatChoice} pricing={this.props.pricing} active={this.state.floatChoice} choiceCount={this.state.floatChoices.length}/>

                        {/* Guide Rails */}
                        <AccessorySummary accessoryType='Guide Rail' accessory={this.state.chosenGuideRail} accessoryClicked={this.resetToGuideRailChoice} pricing={this.props.pricing} active={this.state.guideRailChoice} choiceCount={this.state.guideRailChoices.length}/>

                        {/* Print Packet */}
                        <li className='list-group-item'>
                            <div className='bap-row'>
                                <div className='bap-col-12'>
                                    <Button cssClass='btn btn-sm btn-pump-choice' clicked={this.printPacket}>
                                        <i className='fa fa-print'></i>&nbsp;
                                        Print Packet
                                    </Button>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
                <div id='main' className='bap-col-9'>
                    {this.state.controlPanelChoice ? <div className='bap-col-1' style={{marginBottom: '5px'}}><Button id='skip-btn' cssClass='btn' clicked={this.skipControlPanelChoice}>Skip</Button></div> : null}
                    {this.state.controlPanelChoice ? <AccessoryChoices choices={this.state.controlPanelChoices} choiceMade={this.controlPanelChosen} pricing={this.props.pricing} /> : null}
                    {this.state.capKitChoice ? <AccessoryChoices choices={this.state.capKitChoices} choiceMade={this.capKitChosen} pricing={this.props.pricing} /> : null}
                    {this.state.floatChoice ? <AccessoryChoices choices={this.state.floatChoices} choiceMade={this.floatChosen} pricing={this.props.pricing} /> : null}
                    {this.state.guideRailChoice ? <AccessoryChoices choices={this.state.guideRailChoices} choiceMade={this.guideRailChosen} pricing={this.props.pricing} /> : null}
                    {this.state.complete ? <AccessoryChoicesComplete startOver={this.props.startOver}/> : null}
                </div>
            </div>
        );
    }
}

interface SummaryProps {
    accessoryType: string;
    accessory: Nullable<Accessory>;
    accessoryClicked: () => void;
    pricing: Pricing;
    active: boolean;
    choiceCount: number;
}

/**
 * The AccessorySummary function component summarizes a given accessory type by
 * showing its selected value, as well as its list price.
 */
const AccessorySummary = (props: SummaryProps) : JSX.Element => {
    const getCssClass = () : string => {
        let cssClass = 'list-group-item';
        if (props.active) {
            cssClass += ' current';
        }
        return cssClass;
    }

    return (
        <li className={getCssClass()}>
            <div className='bap-row'>
                <div className='bap-col-12'>
                    <b>{props.accessoryType}:</b>&nbsp;<span id='partNum' onClick={props.accessoryClicked}>{props.accessory? props.accessory.Code : ''}</span>
                </div>
            </div>
            {props.accessory ? null : <div className='bap-row'><div className='bap-col-12'># of choices: {props.choiceCount}</div></div>}
            <div className='bap-row'>
                <div className='bap-col-12'>
                    <PriceDisplay pricing={props.pricing} modelNumber={props.accessory ? props.accessory.Code : ''} />
                </div>
            </div>
        </li>
    );
}

interface AccessoryChoicesProps {
    choices: Accessory[];
    choiceMade: (choice: Accessory) => void;
    pricing: Pricing;
}

/**
 * The AccessoryChoices function component will render all available choices 
 * for the current accessory type.
 */
const AccessoryChoices = (props: AccessoryChoicesProps) : JSX.Element => {
    return (
        <div className='bap-row'>
            {props.choices.map((choice, index) => (
                <AccessoryOption key={index} choice={choice} choiceMade={props.choiceMade} pricing={props.pricing}/>
            ))}
        </div>
    );
}

interface AccessoryOptionProps {
    choice: Accessory;
    choiceMade: (choice: Accessory) => void;
    pricing: Pricing;
}

/**
 * The AccessoryOption function component will render one choice for the current accessory type
 */
const AccessoryOption = (props: AccessoryOptionProps) : JSX.Element => {
    return (
        <div className='accessory-option bap-col-4 mb-2'>
            <div className='card mb-5'>
                <div className='card-body'>
                    <h6 className='text-center'>
                        {props.choice.Code}
                        {props.choice.ImageURL ? <a href={props.choice.ImageURL} target="_blank" rel="noreferrer" style={{margin: "0 0.5em", fontSize: "medium"}}>Image</a> : null}
                    </h6>
                    <hr/>
                    <ul>
                        <li>{props.choice.Description}</li>
                        {props.choice.AdditionalDescription ? <hr style={{width: "75%", margin: "0.5em 0"}}/> : null}
                        {props.choice.AdditionalDescription ? <li><em>*{props.choice.AdditionalDescription}</em></li> : null}
                    </ul>
                    <hr/>
                    <ul>
                        {(props.pricing === Pricing.ALL || props.pricing === Pricing.US) && props.choice.USDListPrice ? <li><small>List Price US</small>&nbsp;<span className='us-price'><b>${props.choice.USDListPrice}</b></span></li> : null}
                        {(props.pricing === Pricing.ALL || props.pricing === Pricing.CA) && props.choice.CADListPrice ? <li><small>List Price CA</small>&nbsp;<span className='ca-price'><b>${props.choice.CADListPrice}</b></span></li> : null}
                    </ul>
                    <Button cssClass='btn btn-pump-choice' clicked={() => {props.choiceMade(props.choice)}}>SELECT</Button>
                </div>
            </div>
        </div>
    );
}

interface CompleteProps {
    startOver: () => void;
}

/**
 * The AccessoryChoicesComplete function component is a static "page" meant to
 * tell the user they've completed the accessory selection process.
 */
const AccessoryChoicesComplete = (props: CompleteProps) : JSX.Element => {
    return (
        <>
            <h3 className='text-center'>
                There are no more matching accessories for this model.
            </h3>
            <h3 className='text-center'>
                Contact Us
            </h3>
            <br/>
            <div style={{width: '100%'}}>
                <h5 className='text-center'>
                    <b>Phone:</b>&nbsp;1-800-543-2550
                </h5>
                <h5 className='text-center'>
                    <b>Fax:</b>&nbsp;1-585-494-1839
                </h5>
                <h5 className='text-center'>
                    <b>Email:</b>&nbsp;
                    <a href='mailto:LEP@libertypumps.com' target='_top' style={{color: '#d2ae7e'}}>LEP@libertypumps.com</a>
                </h5>
                <h5>
                    <div className='bap-container bap-col-2'>
                        <Button cssClass='btn btn-sm btn-pump-choice' clicked={props.startOver}>Start Over</Button>
                    </div>
                </h5>
            </div>
        </>
    );
}

export interface Accessory {
    Code: string;
    Description: string;
    USDListPrice?: number;
    CADListPrice?: number;
    AdditionalDescription?: Nullable<string>;
    ImageURL?: Nullable<string>;
}

const NoControlPanel : Accessory = {
    Code: 'No Control Panel Selected',
    Description: ''
};
